import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { dateInputToISO, isoToDateInput } from "@/lib/dateFormat";

/**
 * Öffnet einen Bearbeiten-Dialog für eine Person direkt in der Personenakte.
 * Reuse-Design analog zu CompanyEditDialog.
 */
export default function ContactEditDialog({ open, onOpenChange, contact, onSaved }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Rehydrate on open transition
  useEffect(() => {
    if (open && contact) {
      setForm({
        salutation: contact.salutation || "",
        title: contact.title || "",
        first_name: contact.first_name || "",
        last_name: contact.last_name || "",
        email: contact.email || "",
        phone: contact.phone || "",
        mobile: contact.mobile || "",
        position: contact.position || "",
        company_id: contact.company_id || "",
        ampel_status: contact.ampel_status || "gruen",
        ampel_grund: contact.ampel_grund || "",
        anker_kennengelernt: contact.anker_kennengelernt || "",
        anker_gemeinsamer_moment: contact.anker_gemeinsamer_moment || "",
        anker_persoenliche_verbindung: contact.anker_persoenliche_verbindung || "",
        naechster_schritt: contact.naechster_schritt || "",
        naechster_termin: contact.naechster_termin ? isoToDateInput(contact.naechster_termin) : "",
        notes: contact.notes || "",
      });
    }
  }, [open, contact]);

  const { data: companies = [] } = useQuery({
    queryKey: ["companies", "all"],
    queryFn: async () => (await api.get(`/companies`)).data,
    enabled: open,
  });

  function setField(name, value) {
    setForm((s) => ({ ...s, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.first_name?.trim() || !form.last_name?.trim()) {
      toast.error("Bitte gib Vor- und Nachnamen an.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        salutation: form.salutation && form.salutation !== "__none__" ? form.salutation : null,
        title: form.title || null,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email || null,
        phone: form.phone || null,
        mobile: form.mobile || null,
        position: form.position || null,
        company_id: form.company_id && form.company_id !== "__none__" ? form.company_id : null,
        ampel_status: form.ampel_status || "gruen",
        ampel_grund: form.ampel_grund || null,
        anker_kennengelernt: form.anker_kennengelernt || null,
        anker_gemeinsamer_moment: form.anker_gemeinsamer_moment || null,
        anker_persoenliche_verbindung: form.anker_persoenliche_verbindung || null,
        naechster_schritt: form.naechster_schritt || null,
        naechster_termin: form.naechster_termin ? dateInputToISO(form.naechster_termin) : null,
        notes: form.notes || null,
      };
      await api.put(`/contacts/${contact.id}`, payload);
      toast.success("Person gespeichert");
      qc.invalidateQueries({ queryKey: ["contact-dossier", contact.id] });
      qc.invalidateQueries({ queryKey: ["contacts"] });
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contact-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Person bearbeiten</DialogTitle>
          <DialogDescription>Änderungen werden nach dem Speichern in der Personenakte übernommen.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Anrede</Label>
              <Select
                value={form.salutation && form.salutation !== "" ? form.salutation : "__none__"}
                onValueChange={(v) => setField("salutation", v === "__none__" ? "" : v)}
              >
                <SelectTrigger className="mt-1" data-testid="contact-edit-field-salutation">
                  <SelectValue placeholder="— Keine —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Keine —</SelectItem>
                  <SelectItem value="Herr">Herr</SelectItem>
                  <SelectItem value="Frau">Frau</SelectItem>
                  <SelectItem value="Divers">Divers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FieldInput label="Titel" id="c-title" value={form.title}
              onChange={(v) => setField("title", v)} testid="contact-edit-field-title" />

            <FieldInput label="Vorname" required id="c-first_name" value={form.first_name}
              onChange={(v) => setField("first_name", v)} testid="contact-edit-field-first_name" />
            <FieldInput label="Nachname" required id="c-last_name" value={form.last_name}
              onChange={(v) => setField("last_name", v)} testid="contact-edit-field-last_name" />
            <FieldInput label="E-Mail" type="email" id="c-email" value={form.email}
              onChange={(v) => setField("email", v)} testid="contact-edit-field-email" />
            <FieldInput label="Telefon" type="tel" id="c-phone" value={form.phone}
              onChange={(v) => setField("phone", v)} testid="contact-edit-field-phone" />
            <FieldInput label="Mobil" type="tel" id="c-mobile" value={form.mobile}
              onChange={(v) => setField("mobile", v)} testid="contact-edit-field-mobile" />
            <FieldInput label="Position" id="c-position" value={form.position}
              onChange={(v) => setField("position", v)} testid="contact-edit-field-position" />

            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Firma</Label>
              <Select
                value={form.company_id ? String(form.company_id) : ""}
                onValueChange={(v) => setField("company_id", v)}
              >
                <SelectTrigger className="mt-1" data-testid="contact-edit-field-company_id">
                  <SelectValue placeholder="Firma wählen…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Keine Firma zuordnen —</SelectItem>
                  {companies.map((co) => (
                    <SelectItem key={co.id} value={co.id}>{co.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Ampelstatus</Label>
              <Select value={form.ampel_status || "gruen"} onValueChange={(v) => setField("ampel_status", v)}>
                <SelectTrigger className="mt-1" data-testid="contact-edit-field-ampel_status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gruen">Grün</SelectItem>
                  <SelectItem value="gelb">Gelb</SelectItem>
                  <SelectItem value="rot">Rot</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FieldInput label="Nächster Termin" type="date" id="c-naechster_termin" value={form.naechster_termin}
              onChange={(v) => setField("naechster_termin", v)} testid="contact-edit-field-naechster_termin" />

            <FieldTextarea label="Ampel: Grund" id="c-ampel_grund" rows={2} value={form.ampel_grund}
              onChange={(v) => setField("ampel_grund", v)} testid="contact-edit-field-ampel_grund" fullWidth />
            <FieldTextarea label="Wie kennengelernt?" id="c-anker_kennen" rows={2} value={form.anker_kennengelernt}
              onChange={(v) => setField("anker_kennengelernt", v)} testid="contact-edit-field-anker_kennengelernt" fullWidth />
            <FieldTextarea label="Gemeinsamer Moment / Anekdote" id="c-anker_moment" rows={2} value={form.anker_gemeinsamer_moment}
              onChange={(v) => setField("anker_gemeinsamer_moment", v)} testid="contact-edit-field-anker_gemeinsamer_moment" fullWidth />
            <FieldTextarea label="Persönliche Verbindung / Interessen" id="c-anker_pers" rows={2} value={form.anker_persoenliche_verbindung}
              onChange={(v) => setField("anker_persoenliche_verbindung", v)} testid="contact-edit-field-anker_persoenliche_verbindung" fullWidth />
            <FieldTextarea label="Nächster Schritt / offene Zusage" id="c-naechster_schritt" rows={2} value={form.naechster_schritt}
              onChange={(v) => setField("naechster_schritt", v)} testid="contact-edit-field-naechster_schritt" fullWidth />
            <FieldTextarea label="Freie Notizen" id="c-notes" rows={4} value={form.notes}
              onChange={(v) => setField("notes", v)} testid="contact-edit-field-notes" fullWidth />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}
              data-testid="contact-edit-cancel-btn">
              Abbrechen
            </Button>
            <Button type="submit" className="bg-black text-white hover:bg-gray-800" disabled={saving}
              data-testid="contact-edit-submit-btn">
              {saving ? "Speichern…" : "Person speichern"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FieldInput({ label, required, id, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-gray-600">
        {label}{required ? <span className="text-rose-500"> *</span> : null}
      </Label>
      <Input
        id={id}
        type={type}
        value={value || ""}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1"
        data-testid={testid}
      />
    </div>
  );
}

function FieldTextarea({ label, id, rows, value, onChange, testid, fullWidth }) {
  return (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-gray-600">
        {label}
      </Label>
      <Textarea
        id={id}
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1"
        data-testid={testid}
      />
    </div>
  );
}
